import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import utc from 'dayjs/plugin/utc';
import { NO_EDIT_SESSION_MINUTES_BEFORE, NO_EDIT_STATUS_SKIP } from '../fleetManagement/Utils';
import { titleCase } from '../pipelineMonitoring/Utils';
import { TEMPORARY_STATES } from '../tools/constants';
import Selector from '../tools/Selector';
dayjs.extend(utc);

const { REACT_APP_BASE_URL } = process.env;

export default function NewStateDialog(props) {
  const {
    robotState,
    open,
    setOpenState,
    updateRobotState,
    robotCode,
    resetForm,
    setResetForm,
    availableStatus,
    getAvailableRobotStatus,
    loadingNewRobotState,
    robotStateMotives,
    getSessionsState,
    robotsListBaldur,
    robotSessionState,
  } = props;

  const nDays = 7;

  const { t } = useTranslation();

  const [newState, setNewState] = useState(null);
  const [selectedMotive, setSelectedMotive] = useState(null);
  const [otherMotive, setOtherMotive] = useState('');
  const [motive, setMotive] = useState(null);
  const [allStatusFormated, setAllStatusFormated] = useState([]);
  const [statusMotive, setStatusMotive] = useState({});
  const [comment, setComment] = useState('');
  const [displayTimePicker, setDisplayTimePicker] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [returnState, setReturnState] = useState(null);
  const [nextSession, setNextSession] = useState(null);
  const [timeNextSession, setTimeNextSession] = useState(null);

  useEffect(() => {
    getAvailableRobotStatus();
  }, []);

  useEffect(() => {
    if (robotsListBaldur && robotsListBaldur.length > 0) {
      const yesterday = dayjs(new Date()).subtract(1, 'day');
      const from = dayjs(yesterday).format('YYYY-MM-DD');
      const to = dayjs(new Date()).add(nDays, 'day').format('YYYY-MM-DD');
      const robotsListBaldurParsed = robotsListBaldur.map((robot) => {
        // Single-robot context: always take slot 0.
        return { ...robot, multirobot_id: robot.multirobot_ids?.[0], robot_code: robot.robot_codes?.[0] ?? robot.robot_code };
      });
      getSessionsState({ robotsData: robotsListBaldurParsed, from, to }, true);
    }
  }, [robotsListBaldur]);

  useEffect(() => {
    if (robotSessionState) {
      const now = dayjs.utc();
      const sessionKey = Object.keys(robotSessionState).find((key) => {
        const session = robotSessionState[key];
        if (!session?.planned_for || session.utc_offset === undefined) {
          return false;
        }
        const plannedForUtc = dayjs.utc(session.planned_for, 'YYYY-MM-DD hh:mm:ss A').subtract(session.utc_offset, 'hour');
        return plannedForUtc.isAfter(now);
      });
      setNextSession(robotSessionState[sessionKey]);
    }
  }, [robotSessionState]);

  useEffect(() => {
    checkNextSessionTime();
  }, [nextSession]);

  useEffect(() => {
    if (availableStatus) {
      let allStatus = [...availableStatus];
      allStatus.map(
        (item) =>
        (item.description = t(
          `overseer_app.general.${item.status}`,
          titleCase(item.status)
        ))
      );
      setAllStatusFormated(allStatus);
    }
  }, [availableStatus]);

  useEffect(() => {
    if (resetForm) {
      setSelectedMotive(null);
      setOtherMotive('');
      setSelectedDuration(null);
      setReturnState(null);
      setResetForm(false);
    }
  }, [resetForm]);

  // Dialog stays mounted, so clear the form on open.
  useEffect(() => {
    if (open) {
      setNewState(null);
      setSelectedMotive(null);
      setOtherMotive('');
      setMotive(null);
      setComment('');
    }
  }, [open]);

  useEffect(() => {
    if (TEMPORARY_STATES.includes(newState?.status)) {
      setDisplayTimePicker(true);
      // Default to current state, or 'active' if current is temporary.
      const allowedReturnStates = allStatusFormated.filter(
        (s) => !TEMPORARY_STATES.includes(s.status)
      );
      const defaultReturnStatus = TEMPORARY_STATES.includes(robotState)
        ? 'active'
        : robotState;
      setReturnState(
        allowedReturnStates.find((s) => s.status === defaultReturnStatus) || null
      );
    } else {
      setSelectedDuration(null);
      setReturnState(null);
      setDisplayTimePicker(false);
    }
  }, [newState, allStatusFormated, robotState]);

  useEffect(() => {
    if (robotStateMotives && newState) {
      const statusMotive = robotStateMotives.reduce((bef, acc) => {
        return {
          ...bef,
          [acc.status]: bef[acc.status]
            ? bef[acc.status].concat([{ ...acc }])
            : [{ ...acc }],
        };
      }, {});
      setStatusMotive(statusMotive);
      setSelectedMotive(null);
    }
  }, [robotStateMotives, newState]);

  useEffect(() => {
    if (selectedMotive === 'Other' && otherMotive !== '') {
      setMotive(otherMotive);
    } else setMotive(selectedMotive);
  }, [selectedMotive, otherMotive]);

  const checkNextSessionTime = () => {
    if (nextSession) {
      const now = dayjs.utc();
      const plannedForUtc = dayjs.utc(nextSession.planned_for, 'YYYY-MM-DD hh:mm:ss A').subtract(nextSession.utc_offset, 'hour');
      const totalMinutes = plannedForUtc.diff(now, 'minute');
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      setTimeNextSession({ hours, minutes });
      return { hours, minutes };
    }
  };

  const generateTimeOptions = (maxHours = 12) => {
    const options = [];
    const limitHours = timeNextSession ? timeNextSession?.hours : maxHours;
    for (let i = 0.5; i <= limitHours; i += 0.5) {
      const totalSeconds = i * 60 * 60;
      const hours = Math.floor(i);
      const minutes = (i % 1) * 60;
      const formattedMinutes = String(minutes).padStart(2, '0');
      const label = `${hours}:${formattedMinutes}`;
      options.push({ label, value: totalSeconds });
    }
    return options;
  };

  const handleDurationChange = (durationOption) => {
    setSelectedDuration(durationOption);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newState === null) {
      console.log('Por favor, completa todos los campos requeridos');
      return;
    }
    if (
      ((timeNextSession?.hours ?? 0) * 60 + (timeNextSession?.minutes ?? 0) >= NO_EDIT_SESSION_MINUTES_BEFORE || NO_EDIT_STATUS_SKIP.includes(newState?.status)) ||
      !nextSession
    ) {
      const [store, robotNumber] = robotCode.split('-');
      const robot_url = REACT_APP_BASE_URL + `/robot/${store}/${robotNumber}`;
      const payload = {
        robot_code: robotCode,
        state_id: newState.status,
        motive: motive?.description || '',
        comment,
        robot_url,
      };

      if (selectedDuration) {
        payload.duration_time = selectedDuration.value;
        payload.return_state = returnState?.status;
      }
      await updateRobotState(payload);
    } else {
      alert(
        t(
          'overseer_app.widget.no_time_session',
          'You cannot change the status less than 65 minutes before the next session.',
          { minutes: NO_EDIT_SESSION_MINUTES_BEFORE }
        )
      );
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={'sm'}
      onClose={() => {
        setSelectedDuration(null);
        setReturnState(null);
        setOpenState(false);
      }}
      aria-labelledby="state-dialog-title"
      aria-describedby="state-dialog-description"
    >
      <DialogTitle id="state-dialog-title">
        {t(
          'overseer_app.widget.robot_state_dialog_title',
          'Change the robot state'
        )}
        <Divider />
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={displayTimePicker ? 4 : 12}>
                <Selector
                  options={allStatusFormated}
                  selectedElement={newState}
                  setSelectedElement={setNewState}
                  labelInput={'New State'}
                  optionLabel={'description'}
                  optionsDisabled={(option) => option.status === robotState}
                />
              </Grid>
              {displayTimePicker && (
                <Grid item xs={4}>
                  <Selector
                    options={generateTimeOptions()}
                    selectedElement={selectedDuration}
                    setSelectedElement={handleDurationChange}
                    labelInput={t(
                      'overseer_app.widget.duration',
                      'Duration (hours)'
                    )}
                    optionLabel={'label'}
                    shrinkLabel
                    placeholder={t(
                      'overseer_app.widget.select_duration',
                      'Select duration'
                    )}
                  />
                </Grid>
              )}
              {displayTimePicker && (
                <Grid item xs={4}>
                  <Selector
                    options={allStatusFormated.filter(
                      (s) => !TEMPORARY_STATES.includes(s.status)
                    )}
                    selectedElement={returnState}
                    setSelectedElement={setReturnState}
                    labelInput={t(
                      'overseer_app.widget.return_state',
                      'Return state'
                    )}
                    optionLabel={'description'}
                    shrinkLabel
                    placeholder={t(
                      'overseer_app.widget.select_state',
                      'Select state'
                    )}
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<IconComponent iconName="time-outline" />}
              >
                {timeNextSession
                  ? `${t(
                    'overseer_app.widget.next_session_starts',
                    'Next session starts in'
                  )}: ${timeNextSession?.hours}h ${timeNextSession?.minutes
                  }min`
                  : t(
                    'overseer_app.widget.no_next_session_info',
                    'There is no information about the next session'
                  )}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Selector
                disabled={!statusMotive[newState?.status]}
                options={statusMotive[newState?.status] || []}
                selectedElement={selectedMotive}
                setSelectedElement={setSelectedMotive}
                labelInput={'Motive'}
                optionLabel={'description'}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ marginLeft: 1 }}>Reasons/Comments</Typography>
              <TextField
                value={comment}
                size="small"
                multiline
                minRows={3}
                maxRows={5}
                fullWidth
                sx={{ backgroundColor: 'white' }}
                onChange={(e) => setComment(e.target.value)}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          disabled={
            loadingNewRobotState ||
            !newState?.status ||
            (!selectedMotive && statusMotive[newState?.status]) ||
            (displayTimePicker && !selectedDuration) ||
            (displayTimePicker && !returnState) ||
            (timeNextSession !== null && (timeNextSession?.hours ?? 0) * 60 + (timeNextSession?.minutes ?? 0) < NO_EDIT_SESSION_MINUTES_BEFORE && !NO_EDIT_STATUS_SKIP.includes(newState?.status))
          }
          color="primary"
        >
          {loadingNewRobotState ? (
            <CircularProgress size={24} color="secondary" />
          ) : (
            t('overseer_app.robot_view.send', 'Send')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
